import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Gallery({ title, subtitle, items, columns = "md:grid-cols-2 lg:grid-cols-3", fit = "cover", header = null }) {
  const [active, setActive] = useState(null);

  const open = (i) => setActive(i);
  const close = () => setActive(null);
  const prev = () => setActive((i) => (i - 1 + items.length) % items.length);
  const next = () => setActive((i) => (i + 1) % items.length);

  return (
    <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16 md:py-24" data-testid={`gallery-${title.toLowerCase()}`}>
      <div className="mb-14 md:mb-20 flex items-end justify-between gap-8 reveal">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-[var(--ember)] mb-4">Selected Works</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-[var(--paper)]">{title}</h1>
          {subtitle && <p className="mt-6 max-w-xl text-[var(--bone)] text-base md:text-lg leading-relaxed">{subtitle}</p>}
          {header}
        </div>
        <div className="hidden md:block text-right text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
          {items.length} pieces
        </div>
      </div>

      <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
        {items.map((it, idx) => (
          <button
            key={it.id}
            onClick={() => open(idx)}
            data-testid={`gallery-item-${it.id}`}
            className="gallery-item group text-left reveal w-full mb-14 break-inside-avoid block"
            style={{ transitionDelay: `${(idx % 6) * 80}ms` }}
          >
            <div className={`overflow-hidden bg-[var(--ink-2)]`}>
              <img
                src={it.src}
                alt={it.title}
                loading="lazy"
                className={`w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.02]${fit === "contain" ? " object-contain p-4 h-auto" : ""}`}
              />
            </div>
            <div className="mt-4 flex items-baseline justify-between gap-4">
              <div>
                <div className="font-serif italic text-xl md:text-2xl text-[var(--paper)]">{it.title}</div>
                <div className="text-xs uppercase tracking-[0.25em] text-[var(--muted)] mt-1">{it.medium}</div>
              </div>
              <div className="text-xs text-[var(--gold)] font-mono">{it.year}</div>
            </div>
          </button>
        ))}
      </div>

      {active !== null && (
        <div role="dialog" data-testid="lightbox" className="fixed inset-0 z-50 bg-[rgba(8,7,5,0.96)] flex items-center justify-center p-4 md:p-10">
          <button data-testid="lightbox-close" onClick={close} className="absolute top-6 right-6 text-[var(--paper)] hover:text-[var(--ember)]">
            <X size={28}/>
          </button>
          <button data-testid="lightbox-prev" onClick={prev} className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 text-[var(--paper)] hover:text-[var(--ember)]">
            <ChevronLeft size={42}/>
          </button>
          <button data-testid="lightbox-next" onClick={next} className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 text-[var(--paper)] hover:text-[var(--ember)]">
            <ChevronRight size={42}/>
          </button>
          <div className="max-w-[1300px] w-full">
            {items[active].rotate ? (
              <div className="mx-auto flex items-center justify-center" style={{ height: "70vh", width: "min(100%, calc(70vh * 1.4))" }}>
                <img
                  src={items[active].src}
                  alt={items[active].title}
                  className="rotate-90 object-contain"
                  style={{ width: "70vh", height: "calc(70vh * 1.4)" }}
                />
              </div>
            ) : (
              <img
                src={items[active].src}
                alt={items[active].title}
                className="w-full max-h-[80vh] object-contain"
              />
            )}
            <div className="mt-6 flex items-baseline justify-between text-[var(--bone)]">
              <div>
                <div className="font-serif italic text-2xl md:text-3xl text-[var(--paper)]">{items[active].title}</div>
                <div className="text-xs uppercase tracking-[0.3em] text-[var(--muted)] mt-1">{items[active].medium}</div>
              </div>
              <div className="text-sm font-mono text-[var(--gold)]">{items[active].year}</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
